import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useState } from "react";
import {
  LabellingCoverageGranularity,
  useGetTenantLabellingCoverageQuery,
} from "store/api/generatedApi";
import { CoverageCalendar } from "./CoverageCalendar";
import { Fetching } from "components/common/loading/fetching";
import { sorted } from "helpers";

interface CalendarTabProps {
  tenantName: string;
  gapTolerance: number;
  granularity: LabellingCoverageGranularity;
}

export const CalendarTab = ({
  tenantName,
  gapTolerance,
  granularity,
}: CalendarTabProps) => {
  const { t } = useTenantTranslation();
  const [calendarMonth, setCalendarMonth] = useState(() => dayjs.utc());

  const calendarStartDate = calendarMonth.startOf("month").toISOString();
  const calendarEndDate = calendarMonth.endOf("month").toISOString();

  const { data, isLoading, isFetching, isError } =
    useGetTenantLabellingCoverageQuery({
      tenantName,
      start: calendarStartDate,
      end: calendarEndDate,
      granularity: granularity,
      gapToleranceSeconds: gapTolerance,
    });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{t("failedToLoadData")}</Typography>
      </Box>
    );
  }

  const locations = sorted(
    Object.entries(data.coverage_by_location),
    (item) => item[0]
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton
          onClick={() => {
            setCalendarMonth(calendarMonth.subtract(1, "month"));
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h6" sx={{ flex: 1, textAlign: "center" }}>
          {calendarMonth.format("MMMM YYYY")}
        </Typography>
        <IconButton
          onClick={() => {
            setCalendarMonth(calendarMonth.add(1, "month"));
          }}
        >
          <ChevronRight />
        </IconButton>
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setCalendarMonth(dayjs.utc());
          }}
        >
          {t("today")}
        </Button>
      </Stack>
      <Fetching fetching={isFetching}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t("tenantOverview")}
          </Typography>
          <CoverageCalendar buckets={data.buckets} month={calendarMonth} />
        </Paper>

        {locations.map(([locationName, locationData]) => (
          <Paper key={locationName} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {locationName}
            </Typography>
            <CoverageCalendar
              buckets={locationData.buckets}
              month={calendarMonth}
            />
          </Paper>
        ))}
      </Fetching>
    </Stack>
  );
};
